import { useEffect, useRef } from "react";
import styles from "./FormatsRecallControl.module.css";

const ChevronIcon = ({ className, expanded }) => (
  <svg
    className={`${className} ${expanded ? styles.chevronOpen : ""}`}
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
  >
    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const getFormatResolutionLabel = (format) => {
  const width = Number(format?.res_width);
  const height = Number(format?.res_height);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return "";
  }
  return `${width}\u00D7${height}`;
};

const FormatsRecallControl = ({
  currentFormatLabel,
  currentFormatResolution,
  formats,
  formatMetaByName,
  selectedFormat,
  onSelectFormat,
  isOpen,
  onOpen,
  onClose,
}) => {
  const rootRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        onClose();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  return (
    <div ref={rootRef} className={styles.root}>
      <div className={styles.bar}>
        <div className={styles.currentBlock}>
          <span className={styles.currentEyebrow}>Current format</span>
          <div className={styles.currentValueBlock}>
            <span className={styles.currentValue}>{currentFormatLabel}</span>
            {currentFormatResolution && (
              <span className={styles.currentMeta}>{currentFormatResolution}</span>
            )}
          </div>
        </div>
        <button
          type="button"
          className={styles.trigger}
          onClick={isOpen ? onClose : onOpen}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          aria-controls="formats-recall-panel"
        >
          <span>Change format</span>
          <ChevronIcon className={styles.chevron} expanded={isOpen} />
        </button>
      </div>

      {isOpen && (
        <div id="formats-recall-panel" className={styles.panel} role="dialog" aria-label="Change format">
          <div className={styles.options}>
            {formats.map((format) => {
              const formatMeta = formatMetaByName.get(format);
              const formatResolution = format === "all" ? "" : getFormatResolutionLabel(formatMeta);
              const isSelected = selectedFormat === format;

              return (
                <button
                  key={format}
                  type="button"
                  className={`${styles.option} ${isSelected ? styles.optionSelected : ""}`}
                  onClick={() => onSelectFormat(format)}
                  aria-pressed={isSelected}
                >
                  <span className={styles.optionTextBlock}>
                    <span className={styles.optionTitle}>{format === "all" ? "All formats" : format}</span>
                    {formatResolution && (
                      <span className={styles.optionMeta}>{formatResolution}</span>
                    )}
                  </span>
                  {isSelected && <span className={styles.optionStatus}>Current</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default FormatsRecallControl;
